import { useQuery } from '@tanstack/react-query'
import { adminService } from '../services/adminService'
import { StatsGrid } from '../components/StatsGrid'
import { RecentActivity } from '../components/RecentActivity'
import { useTranslation } from '../i18n/hooks/useTranslation'

export function AnalyticsPage() {
  const { t } = useTranslation()
  
  const { data: stats, isLoading } = useQuery({
    queryKey: ['adminStats'],
    queryFn: () => adminService.getAdminStats(),
  })

  const { data: recentChallenges = [] } = useQuery({
    queryKey: ['recentChallenges'],
    queryFn: () => adminService.getRecentChallenges(5),
  })

  const { data: recentUsers = [] } = useQuery({
    queryKey: ['recentUsers'],
    queryFn: () => adminService.getRecentUsers(5),
  })

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('common.loading')}</div>
        </div>
      </div>
    )
  }

  const growthMetrics = {
    usersGrowth: '+12%',
    challengesGrowth: '+8%',
    categoriesGrowth: '+2%',
  }

  return (
    <div className="p-8">
      {/* Enhanced Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
              {t('analytics.title')}
            </h1>
            <p className="text-gray-600 mt-2 text-sm lg:text-base leading-relaxed">
              {t('analytics.subtitle')}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 rounded-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="text-sm font-medium">Live Analytics</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Stats */}
      <div className="mb-8">
        <StatsGrid stats={stats} clickable={false} />
      </div>

      {/* Growth Metrics */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('analytics.growthTrends')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">{t('analytics.userGrowth')}</p>
                <p className="text-2xl font-bold text-gray-900">{growthMetrics.usersGrowth}</p>
              </div>
              <div className="text-gray-600">📈</div>
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('analytics.vsLastMonth')}</p>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">{t('analytics.challengeGrowth')}</p>
                <p className="text-2xl font-bold text-gray-900">{growthMetrics.challengesGrowth}</p>
              </div>
              <div className="text-gray-600">🎯</div>
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('analytics.vsLastMonth')}</p>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">{t('analytics.challengeGrowth')}</p>
                <p className="text-2xl font-bold text-gray-900">{growthMetrics.challengesGrowth}</p>
              </div>
              <div className="text-gray-600">📝</div>
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('analytics.vsLastMonth')}</p>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">{t('analytics.categoryGrowth')}</p>
                <p className="text-2xl font-bold text-gray-900">{growthMetrics.categoriesGrowth}</p>
              </div>
              <div className="text-gray-600">📂</div>
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('analytics.vsLastMonth')}</p>
          </div>
        </div>
      </div>

      {/* User Engagement Metrics */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('analytics.platformHealth')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('analytics.activeUsers')}</h3>
            <div className="flex items-center">
              <span className="text-3xl font-bold text-gray-900">
                {stats ? Math.round(stats.usersCount * 0.75) : 0}
              </span>
              <span className="text-sm text-gray-500 ml-2">/ {stats?.usersCount || 0} {t('common.total').toLowerCase()}</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">{t('analytics.usersActiveInLast30Days')}</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('analytics.completionRate')}</h3>
            <div className="flex items-center">
              <span className="text-3xl font-bold text-gray-900">68%</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">{t('analytics.avgChallengeCompletionRate')}</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('analytics.challengesUsage')}</h3>
            <div className="flex items-center">
              <span className="text-3xl font-bold text-gray-900">
                {stats ? Math.round(stats.challengesCount * 0.85) : 0}
              </span>
              <span className="text-sm text-gray-500 ml-2">/ {stats?.challengesCount || 0} {t('common.total').toLowerCase()}</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">{t('analytics.challengesActivelyUsed')}</p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <RecentActivity 
        recentChallenges={recentChallenges} 
        recentUsers={recentUsers}
        showUsers={true} 
      />
    </div>
  )
}